import {jsPDF} from "jspdf";
import {formatNumber, formatGeneratedAt} from "../../digitization/crosssection/reportFormat.js";
import {drawReportLogo} from "../../digitization/crosssection/reportLogo.js";
import {sanitizeFilenameSegment, stripFileExtension} from "../../digitization/crosssection/reportFilename.js";

import {t, numberLocale} from "../../digitization/i18n/index.js";
// Graverapport som PDF, bygget helt i browseren med jsPDF - samme stil og samme side-/sidefodsopbygning som Tværprofilrapporten
// (digitization/crosssection/CrossSectionPdfBuilder.js): A4, titel + IT34-mærke, metadata-felter, nøgletal, topbillede i punktskyens
// farver med udgravning/påfyldning markeret, dybdekort og en metodeside. Ingen browser-print (ingen URL/dato fra browseren).
//
//   Potree.buildGraveReportPdf(result, {name, dataSource, coordinateSystem}) -> {filename}   (gemmer filen)

const PAGE_WIDTH = 210, PAGE_HEIGHT = 297;
const MARGIN = 14;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const FOOTER_BASELINE_Y = PAGE_HEIGHT - 8;
const FOOTER_RULE_Y = PAGE_HEIGHT - 12;

const BLUE = [47, 143, 214];
const CUT_COLOR = [217, 38, 38];
const FILL_COLOR = [34, 160, 80];

// Dybde-/højdeskalaer som visningen (CutFillVolume.js): udgravning blå -> gul -> rød (dyb), påfyldning grøn -> blågrøn -> blå
const CUT_RAMP = [[51, 115, 230], [242, 217, 51], [217, 38, 38]];
const FILL_RAMP = [[140, 217, 115], [38, 166, 140], [64, 89, 217]];

function ramp(stops, t){
	const clamped = Math.min(1, Math.max(0, t));
	const scaled = clamped * (stops.length - 1);
	const i = Math.min(stops.length - 2, Math.floor(scaled));
	const f = scaled - i;
	return stops[i].map((v, k) => Math.round(v + (stops[i + 1][k] - v) * f));
}

const rgbCss = (c, a = 1) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;

function makeCanvas(width, height){
	const canvas = document.createElement("canvas");
	canvas.width = Math.max(1, Math.round(width));
	canvas.height = Math.max(1, Math.round(height));
	return canvas;
}

// Kanten af et område (celler med mindst én nabo uden for området) som korte linjestykker i billedkoordinater.
function strokeMaskOutline(ctx, region, toPx, color, lineWidth){
	const {nx, ny, x0, y0, cell, mask} = region.grid;
	ctx.strokeStyle = rgbCss(color);
	ctx.lineWidth = lineWidth;
	ctx.beginPath();
	const at = (r, c) => (r < 0 || c < 0 || r >= ny || c >= nx) ? 0 : mask[r * nx + c];
	for(let r = 0; r < ny; r++){
		for(let c = 0; c < nx; c++){
			if(!mask[r * nx + c]) continue;
			const [ax, ay] = toPx(x0 + c * cell, y0 + r * cell);
			const [bx, by] = toPx(x0 + (c + 1) * cell, y0 + (r + 1) * cell);
			if(!at(r - 1, c)){ ctx.moveTo(ax, ay); ctx.lineTo(bx, ay); } // sydkant (lavere y = længere nede i billedet = større pixel-y)
			if(!at(r + 1, c)){ ctx.moveTo(ax, by); ctx.lineTo(bx, by); } // nordkant
			if(!at(r, c - 1)){ ctx.moveTo(ax, ay); ctx.lineTo(ax, by); }
			if(!at(r, c + 1)){ ctx.moveTo(bx, ay); ctx.lineTo(bx, by); }
		}
	}
	ctx.stroke();
}

function fillMaskCells(ctx, region, toPx, color){
	const {nx, ny, x0, y0, cell, mask} = region.grid;
	ctx.fillStyle = color;
	for(let r = 0; r < ny; r++){
		for(let c = 0; c < nx; c++){
			if(!mask[r * nx + c]) continue;
			const [ax, ay] = toPx(x0 + c * cell, y0 + r * cell);
			const [bx, by] = toPx(x0 + (c + 1) * cell, y0 + (r + 1) * cell);
			ctx.fillRect(Math.min(ax, bx), Math.min(ay, by), Math.abs(bx - ax) + 0.6, Math.abs(by - ay) + 0.6);
		}
	}
}

function niceLength(maxMeters){
	const steps = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
	let best = steps[0];
	for(const s of steps) if(s <= maxMeters) best = s;
	return best;
}

// Topbillede (nord op) i punktskyens egne farver, med udgravning (rød) og påfyldning (grøn) markeret, målestok og nordpil.
export function renderOverviewCanvas(result){
	const preview = result.preview;
	const w = preview ? preview.width : 800;
	const h = preview ? preview.height : 600;
	const canvas = makeCanvas(w, h);
	const ctx = canvas.getContext("2d");

	ctx.fillStyle = "#ffffff";
	ctx.fillRect(0, 0, canvas.width, canvas.height);

	let toPx;
	let pixelMeters;
	if(preview){
		const image = ctx.createImageData(preview.width, preview.height);
		for(let i = 0, n = preview.width * preview.height; i < n; i++){
			image.data[i * 4] = preview.rgb[i * 3];
			image.data[i * 4 + 1] = preview.rgb[i * 3 + 1];
			image.data[i * 4 + 2] = preview.rgb[i * 3 + 2];
			image.data[i * 4 + 3] = 255;
		}
		ctx.putImageData(image, 0, 0);
		pixelMeters = preview.pixel;
		toPx = (x, y) => [(x - preview.x0) / preview.pixel, (preview.yTop - y) / preview.pixel];
	}else{
		// ingen farver i skyen: grå flade med områderne
		ctx.fillStyle = "#e6e6e6";
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		const ext = result.meta.extent;
		pixelMeters = Math.max(ext.xMax - ext.xMin, ext.yMax - ext.yMin, 1e-6) / Math.max(w, h);
		toPx = (x, y) => [(x - ext.xMin) / pixelMeters, (ext.yMax - y) / pixelMeters];
	}

	const lineWidth = Math.max(2, Math.round(Math.max(w, h) / 450));
	for(const [kind, color] of [["cut", CUT_COLOR], ["fill", FILL_COLOR]]){
		const region = result[kind];
		if(!region || region.volume_m3 <= 0 || region.grid.nx === 0) continue;
		fillMaskCells(ctx, region, toPx, rgbCss(color, 0.28));
		strokeMaskOutline(ctx, region, toPx, color, lineWidth);
	}

	// målestok (nederst til venstre)
	const meters = niceLength(Math.max(w, h) * pixelMeters * 0.25);
	const barPx = meters / pixelMeters;
	const bx = Math.round(w * 0.03), by = h - Math.round(h * 0.04);
	ctx.fillStyle = "rgba(255,255,255,0.8)";
	ctx.fillRect(bx - 8, by - 28, barPx + 16, 44);
	ctx.fillStyle = "#222";
	ctx.fillRect(bx, by, barPx, 5);
	ctx.font = `${Math.max(14, Math.round(w / 70))}px sans-serif`;
	ctx.fillText(`${meters} m`, bx, by - 8);

	// nordpil (øverst til højre)
	const nx = w - Math.round(w * 0.05), ny = Math.round(h * 0.12);
	const arm = Math.max(18, Math.round(w / 40));
	ctx.fillStyle = "rgba(255,255,255,0.8)";
	ctx.fillRect(nx - arm, ny - arm * 1.4, arm * 2, arm * 2.6);
	ctx.fillStyle = "#222";
	ctx.beginPath();
	ctx.moveTo(nx, ny - arm * 1.1);
	ctx.lineTo(nx - arm * 0.45, ny + arm * 0.5);
	ctx.lineTo(nx, ny + arm * 0.2);
	ctx.lineTo(nx + arm * 0.45, ny + arm * 0.5);
	ctx.closePath();
	ctx.fill();
	ctx.font = `bold ${Math.max(14, Math.round(w / 60))}px sans-serif`;
	ctx.textAlign = "center";
	ctx.fillText("N", nx, ny + arm * 1.1);
	ctx.textAlign = "start";

	return canvas;
}

// Dybde-/højdekort for ét område: hver celle farvet efter |reference - overflade|, med skala.
export function renderDepthCanvas(region, kind){
	const g = region.grid;
	const scale = Math.max(2, Math.min(14, Math.floor(700 / Math.max(g.nx, g.ny))));
	const legendHeight = 46;
	// mindst 360 px bredt, så skalaen og teksten får samme størrelse på alle kort (kortet centreres)
	const mapWidth = g.nx * scale;
	const canvas = makeCanvas(Math.max(mapWidth, 360), g.ny * scale + legendHeight);
	const mapX = Math.round((canvas.width - mapWidth) / 2);
	const ctx = canvas.getContext("2d");
	ctx.fillStyle = "#ffffff";
	ctx.fillRect(0, 0, canvas.width, canvas.height);

	const stops = kind === "cut" ? CUT_RAMP : FILL_RAMP;
	const maxDepth = Math.max(region.max_depth_m, 1e-6);
	for(let r = 0; r < g.ny; r++){
		for(let c = 0; c < g.nx; c++){
			const i = r * g.nx + c;
			if(!g.mask[i]) { ctx.fillStyle = "#f1f1f1"; }
			else{
				const depth = Math.abs(g.reference[i] - g.surface[i]);
				ctx.fillStyle = rgbCss(ramp(stops, depth / maxDepth));
			}
			// nord op: højeste r tegnes øverst
			ctx.fillRect(mapX + c * scale, (g.ny - 1 - r) * scale, scale + 0.5, scale + 0.5);
		}
	}

	const barY = g.ny * scale + 14, barW = canvas.width - 20, barX = 10;
	for(let i = 0; i < barW; i++){
		ctx.fillStyle = rgbCss(ramp(stops, i / (barW - 1)));
		ctx.fillRect(barX + i, barY, 1, 12);
	}
	ctx.fillStyle = "#222";
	ctx.font = "14px sans-serif";
	ctx.fillText("0 m", barX, barY + 28);
	const label = `${maxDepth.toFixed(2)} m`;
	ctx.fillText(label, barX + barW - ctx.measureText(label).width, barY + 28);
	return canvas;
}

function metaLine(doc, x, y, label, value){
	doc.setFont(undefined, "normal");
	doc.setFontSize(7.5);
	doc.setTextColor(120, 120, 120);
	doc.text(label.toUpperCase(), x, y);
	doc.setFont(undefined, "bold");
	doc.setFontSize(10);
	doc.setTextColor(30, 30, 30);
	const lines = doc.splitTextToSize(String(value), 44);
	doc.text(lines[0], x, y + 4.3);
}

function regionCard(doc, x, y, width, title, color, region, offset, isCut){
	doc.setDrawColor(210, 210, 210);
	doc.setLineWidth(0.2);
	doc.rect(x, y, width, 40);
	doc.setFillColor(...color);
	doc.rect(x, y, 1.6, 40, "F");

	doc.setFont(undefined, "bold");
	doc.setFontSize(11);
	doc.setTextColor(...color);
	doc.text(title, x + 6, y + 7);

	if(!region || region.volume_m3 <= 0){
		doc.setFont(undefined, "normal");
		doc.setFontSize(10);
		doc.setTextColor(120, 120, 120);
		doc.text(t("Ikke fundet"), x + 6, y + 16);
		return;
	}

	doc.setFont(undefined, "bold");
	doc.setFontSize(18);
	doc.setTextColor(20, 20, 20);
	doc.text(`${formatNumber(region.volume_m3, 1)} m³`, x + 6, y + 17);

	doc.setFont(undefined, "normal");
	doc.setFontSize(9);
	doc.setTextColor(70, 70, 70);
	doc.text(t("Areal: {0} m²", formatNumber(region.area_m2, 1)), x + 6, y + 24);
	doc.text(`${isCut ? t("Maks. dybde") : t("Maks. højde")}: ${formatNumber(region.max_depth_m, 2)} m`, x + 6, y + 29);
	const cx = region.centroid[0] + offset[0], cy = region.centroid[1] + offset[1];
	doc.text(t("Midtpunkt: {0} / {1}", formatNumber(cx, 1), formatNumber(cy, 1)), x + 6, y + 34);
}

function drawFooters(doc){
	const pageCount = doc.internal.getNumberOfPages();
	for(let p = 1; p <= pageCount; p++){
		doc.setPage(p);
		doc.setDrawColor(225, 225, 225);
		doc.setLineWidth(0.2);
		doc.line(MARGIN, FOOTER_RULE_Y, PAGE_WIDTH - MARGIN, FOOTER_RULE_Y);
		doc.setFont(undefined, "normal");
		doc.setFontSize(8);
		doc.setTextColor(140, 140, 140);
		doc.text("IT34 · SmartSurvey", MARGIN, FOOTER_BASELINE_Y);
		doc.text(t("Side {0} af {1}", p, pageCount), PAGE_WIDTH - MARGIN, FOOTER_BASELINE_Y, {align: "right"});
	}
}

export function graveReportFilename(dataSource){
	if(!dataSource) return t("Graverapport.pdf");
	const base = sanitizeFilenameSegment(stripFileExtension(dataSource));
	return base ? t("Graverapport_{0}.pdf", base) : t("Graverapport.pdf");
}

// result: resultatet fra computeCutFillVolume(); info: {name, dataSource, coordinateSystem, project}
export function buildGraveReportPdf(result, info = {}){
	const doc = new jsPDF({unit: "mm", format: "a4", orientation: "portrait"});
	const meta = result.meta;
	const plan = result.plan || {};
	const offset = plan.offset || [0, 0, 0];
	const title = info.name || t("Punktsky");

	// ---- side 1
	let y = MARGIN;
	doc.setFont(undefined, "bold");
	doc.setFontSize(20);
	doc.setTextColor(15, 15, 15);
	doc.text(t("Graverapport"), MARGIN, y + 7);
	doc.setFont(undefined, "normal");
	doc.setFontSize(11);
	doc.setTextColor(70, 70, 70);
	doc.text(title, MARGIN, y + 13);

	// IT_thirty four-logoet i stedet for tekstmærket "IT34 · SmartSurvey"
	const logoX = drawReportLogo(doc, PAGE_WIDTH, MARGIN, y);
	doc.setFont(undefined, "normal");
	doc.setFontSize(8.5);
	doc.setTextColor(130, 130, 130);
	doc.text(t("Automatisk volumenberegning fra punktsky"), logoX - 4, y + 9, {align: "right"});

	y += 17;
	doc.setDrawColor(...BLUE);
	doc.setLineWidth(0.8);
	doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
	y += 7;

	const items = [
		[t("Genereret"), formatGeneratedAt()],
		info.dataSource ? [t("Datakilde"), info.dataSource] : null,
		info.project ? [t("Projekt"), info.project] : null,
		info.coordinateSystem ? [t("Koordinatsystem"), info.coordinateSystem] : null,
		[t("Cellestørrelse"), `${formatNumber(meta.cell, 2)} m`],
		[t("Oprindeligt terræn"), meta.referenceMethod === "ground" ? t("Jordpunkter (klasse 2)") : t("Scanningens yderrand")],
		[t("Punkter anvendt"), meta.points.toLocaleString(numberLocale())],
		plan.level !== undefined ? [t("Oktræ-niveau"), t("{0} (ca. {1} punkter/m²)", plan.level, Math.round(plan.density).toLocaleString(numberLocale()))] : null,
	].filter(Boolean);
	const cols = 4, colWidth = CONTENT_WIDTH / cols;
	items.forEach(([label, value], i) => metaLine(doc, MARGIN + (i % cols) * colWidth, y + Math.floor(i / cols) * 11, label, value));
	y += Math.ceil(items.length / cols) * 11 + 4;

	const cardWidth = (CONTENT_WIDTH - 6) / 2;
	regionCard(doc, MARGIN, y, cardWidth, t("Udgravning"), CUT_COLOR, result.cut, offset, true);
	regionCard(doc, MARGIN + cardWidth + 6, y, cardWidth, t("Påfyldning"), FILL_COLOR, result.fill, offset, false);
	y += 46;

	const overview = renderOverviewCanvas(result);
	const overviewHeight = Math.min(CONTENT_WIDTH * (overview.height / overview.width), PAGE_HEIGHT - y - 24);
	const overviewWidth = overviewHeight * (overview.width / overview.height);
	const overviewX = MARGIN + (CONTENT_WIDTH - overviewWidth) / 2;
	doc.addImage(overview, "PNG", overviewX, y, overviewWidth, overviewHeight, undefined, "FAST");
	doc.setDrawColor(200, 200, 200);
	doc.setLineWidth(0.2);
	doc.rect(overviewX, y, overviewWidth, overviewHeight);
	y += overviewHeight + 5;

	doc.setFont(undefined, "normal");
	doc.setFontSize(8.5);
	doc.setTextColor(110, 110, 110);
	doc.setFillColor(...CUT_COLOR); doc.rect(MARGIN, y - 2.6, 3.5, 3.5, "F");
	doc.text(t("Udgravning"), MARGIN + 5.5, y);
	doc.setFillColor(...FILL_COLOR); doc.rect(MARGIN + 32, y - 2.6, 3.5, 3.5, "F");
	doc.text(t("Påfyldning"), MARGIN + 37.5, y);
	doc.text(t("Topbillede i punktskyens farver, nord op."), PAGE_WIDTH - MARGIN, y, {align: "right"});

	// ---- side 2: dybdekort + metode
	doc.addPage();
	y = MARGIN;
	doc.setFont(undefined, "bold");
	doc.setFontSize(11);
	doc.setTextColor(15, 15, 15);
	doc.text(t("Graverapport — {0} (fortsat)", title), MARGIN, y + 4);
	y += 7;
	doc.setDrawColor(...BLUE);
	doc.setLineWidth(0.5);
	doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
	y += 8;

	// dybdekort: kun for områder af en vis størrelse (en minimal påfyldning på få celler er kun et tal i kortet på side 1);
	// to kort står side om side, ét kort får mere plads
	const maps = [["cut", t("Dybde af udgravningen"), result.cut], ["fill", t("Højde af påfyldningen"), result.fill]]
		.filter(([, , region]) => region && region.volume_m3 >= 0.1 && region.grid.nx * region.grid.ny >= 25);
	if(maps.length > 0){
		const columns = maps.length;
		const columnWidth = (CONTENT_WIDTH - 8 * (columns - 1)) / columns;
		const maxHeight = columns === 1 ? 140 : 120;
		let rowHeight = 0;
		maps.forEach(([kind, label, region], index) => {
			const canvas = renderDepthCanvas(region, kind);
			const aspect = canvas.width / canvas.height;
			const w = Math.min(columnWidth, maxHeight * aspect);
			const h = w / aspect;
			const x = MARGIN + index * (columnWidth + 8);
			doc.setFont(undefined, "bold");
			doc.setFontSize(10);
			doc.setTextColor(30, 30, 30);
			doc.text(label, x, y);
			doc.addImage(canvas, "PNG", x, y + 3, w, h, undefined, "FAST");
			doc.setDrawColor(200, 200, 200);
			doc.rect(x, y + 3, w, h);
			rowHeight = Math.max(rowHeight, h + 3);
		});
		y += rowHeight + 8;
	}

	if(y + 60 > PAGE_HEIGHT - 16){ doc.addPage(); y = MARGIN; }
	doc.setFont(undefined, "bold");
	doc.setFontSize(10);
	doc.setTextColor(30, 30, 30);
	doc.text(t("Metode"), MARGIN, y);
	y += 5;
	const lines = [
		t("Punktskyen er inddelt i et net af {0} m celler, hvor cellens højde er medianen af punkterne i cellen.", formatNumber(meta.cell, 2)),
		meta.referenceMethod === "ground"
			? t("Det oprindelige terræn er rekonstrueret som en jævn flade gennem punkter, der allerede er klassificeret som jord ({0} kontrolceller, renset for lokale afvigere).", meta.controlPoints.toLocaleString(numberLocale()))
			: t("Punktskyen har for få jord-klassificerede punkter, så det oprindelige terræn er rekonstrueret ud fra den scannede flades yderrand."),
		t("Celler under det oprindelige terræn er udgravet materiale, celler over er påfyldt. For hver er det største sammenhængende område medtaget (mindste dybde {0} m), og volumen er summen af højdeforskellen gange celleareal.", formatNumber(meta.minDepth, 2)),
		t("Støjpunkter (klasse 7) er fjernet: {0}.", meta.noisePoints.toLocaleString(numberLocale())),
		t("Beregningen er udført på et grovere niveau af punktskyens oktræ (niveau {0}, {1} punkter, ca. {2} punkter pr. m²), så resultatet kan afvige nogle procent fra en beregning på fuld opløsning.", plan.level ?? "?", meta.points.toLocaleString(numberLocale()), Math.round(plan.density || 0).toLocaleString(numberLocale())),
	];
	if(plan.lowDensity){
		lines.push(t("OBS: punktskyen er tyndere, end der er behov for til en sikker beregning (eller punktbudgettet er nået) - volumen kan afvige mere end normalt og bør kontrolleres."));
	}
	doc.setFont(undefined, "normal");
	doc.setFontSize(9);
	doc.setTextColor(70, 70, 70);
	for(const paragraph of lines){
		const wrapped = doc.splitTextToSize(paragraph, CONTENT_WIDTH);
		doc.text(wrapped, MARGIN, y);
		y += wrapped.length * 4.2 + 1.6;
	}

	drawFooters(doc);

	const filename = graveReportFilename(info.dataSource || info.name);
	if(info.output === "blob"){
		return {filename, blob: doc.output("blob")}; // (til test/forhåndsvisning: gem ikke)
	}
	doc.save(filename);
	return {filename};
}
